import { useLayoutEffect, type RefObject } from 'react';

/**
 * Faire paraitre une grille par vagues, de haut en bas.
 *
 * Une grille de quarante tuiles apparaissait d'un bloc : rien ne se
 * passait, tout etait deja la. En les faisant monter l'une apres l'autre,
 * l'oeil suit l'ordre de lecture et la collection se donne a voir au lieu
 * de s'afficher.
 *
 * # Ce qui entre dans l'ecran, et rien d'autre
 *
 * Animer les quarante d'un coup ferait defiler sur une page ou tout a
 * deja bouge, sans que personne l'ait vu. Un `IntersectionObserver` ne
 * declenche donc que ce qui est visible : la premiere vague au montage,
 * puis une nouvelle a chaque fois qu'on descend. Une tuile qui a paru est
 * oubliee de l'observateur — on ne rejoue pas la scene en remontant.
 *
 * # L'ordre
 *
 * Les entrees arrivent dans l'ordre du DOM, qui n'est pas toujours celui
 * de l'oeil. On les retrie par position — du haut vers le bas, puis de
 * gauche a droite — et le rang ainsi obtenu devient le retard de chacune.
 *
 * # Une liste qui change
 *
 * Filtrer, chercher, recharger : les elements vont et viennent. Le
 * crochet se rejoue donc a chaque changement de `cle`, mais ne prend que
 * ce qui n'a pas encore paru — une tuile deja vue n'a aucune raison de
 * recommencer parce que sa voisine est arrivee.
 *
 * # Pas pendant un balayage
 *
 * L'ecran voisin est monte des qu'il pointe le bout du nez, bien avant
 * qu'on arrive dessus : la vague partait donc au milieu du geste, sur une
 * bande encore de travers, et le temps d'arriver on l'avait vue passer
 * deux fois sans jamais la voir en entier. `pret` la retient jusqu'a ce
 * que le balayage soit pose — appuyer sur l'onglet, lui, ne retient rien.
 *
 * Cela vaut pour les deux balayages. Celui des onglets etait retenu, pas
 * celui du retour : sortir d'une fiche de trophee en tirant depuis le
 * bord montait la grille derriere le doigt, la vague partait aussitot, et
 * l'on voyait les tuiles paraitre deux fois.
 *
 * # La premiere vague ne passe pas par l'observateur
 *
 * `IntersectionObserver` ne repond jamais avant la peinture : il livre sa
 * premiere reponse une image ou deux plus tard. La grille restait donc
 * vide ce laps de temps, et au retour d'une fiche — ou rien ne glisse
 * par-dessus — cela se voyait comme un chargement.
 *
 * Ce qui est visible au montage se decide donc a la main, par une simple
 * comparaison de rectangles, dans un effet **de mise en page** : avant
 * que quoi que ce soit ne soit peint. L'observateur ne garde que son vrai
 * role, celui pour lequel il est irremplacable — dire quand une tuile
 * entre dans l'ecran pendant qu'on descend.
 *
 * # Jouer, attendre, ou poser
 *
 * `pret` fait attendre — le temps d'un balayage d'onglets. Quand la
 * vague n'a pas lieu, les tuiles sont posees telles quelles : sans
 * animation, et surtout sans rester invisibles — c'est le fond du sujet,
 * `[data-cascade]` naissant transparent.
 *
 * # Apres coup, l'animation s'efface
 *
 * Un `animation-fill-mode: both` laisse la transformee finale posee sur
 * l'element, et elle l'emporterait sur celle de l'appui — la tuile ne
 * s'enfoncerait plus sous le doigt. La classe est donc retiree des la fin
 * de l'animation, remplacee par un simple « deja vue ».
 */
/**
 * Quand chaque grille a joue sa vague pour la derniere fois.
 *
 * Un ecran peut se remonter sans qu'on soit reellement revenu dessus —
 * un geste de retour en cours de route, une bande qui change de forme.
 * Rejouer la vague donne alors un second chargement, et c'est ce qu'on
 * voyait. Une grille qui vient de paraitre se pose donc telle quelle.
 *
 * Le repere vit hors du composant, puisque c'est precisement le
 * composant qui se remonte.
 *
 * # L'empreinte est le nom de l'ecran, pas son adresse
 *
 * On se reperait sur `window.location.pathname`, et c'etait faux pour le
 * seul cas qui compte. Pendant un balayage de retour, la grille se monte
 * **avant** que la navigation ait eu lieu : l'adresse designe encore la
 * fiche qu'on quitte. Le montage suivant, une fois la navigation faite,
 * portait donc une autre empreinte — le garde-fou ne rapprochait pas les
 * deux, et la vague repassait. C'est la double apparition.
 *
 * Le nom vient de l'appelant et ne bouge jamais : deux montages du meme
 * ecran se reconnaissent, quelle que soit l'adresse du moment.
 */
const dernieresVagues = new Map<string, number>();

/** En deca, on considere qu'on n'a jamais quitte l'ecran. */
const REPOS_VAGUE_MS = 1500;

export function useCascade(
  /** Le nom de l'ecran : « trophees », « evenements »… */
  nom: string,
  grille: RefObject<HTMLElement | null>,
  cle: unknown,
  pret = true,
): void {
  useLayoutEffect(() => {
    const racine = grille.current;
    if (!pret || !racine) return;

    /* Ni ce qui a deja paru, ni ce qui est en train de paraitre. */
    const tuiles = Array.from(
      racine.querySelectorAll<HTMLElement>('[data-cascade]:not(.cascade--vue):not(.cascade--entre)'),
    );
    if (tuiles.length === 0) return;

    /*
      Une vague qui vient de passer ne repasse pas.

      Deux montages du meme ecran a une seconde d'intervalle, c'est un
      aller-retour, pas une arrivee.
    */
    const derniere = dernieresVagues.get(nom) ?? 0;
    const trop = Date.now() - derniere < REPOS_VAGUE_MS;
    dernieresVagues.set(nom, Date.now());

    /* Qui ne veut pas d'animation la recoit finie. */
    if (trop || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      tuiles.forEach((tuile) => tuile.classList.add('cascade--vue'));
      return;
    }

    /*
      La zone defilante sert de cadre, et non la fenetre : c'est elle qui
      decide de ce qu'on voit. La marge basse retient la vague jusqu'a ce
      que la tuile soit franchement entree, plutot qu'au premier pixel.
    */
    const cadre = racine.closest<HTMLElement>('.scroll-area');

    /*
      Ce qui est deja sous les yeux, mesure tout de suite.

      Une comparaison de rectangles, ici et maintenant : la tuile est-elle
      dans le cadre ? Les tuiles concernees partent des cette image, sans
      attendre la reponse de l'observateur.
    */
    const vue = cadre?.getBoundingClientRect();
    const hautCadre = vue?.top ?? 0;
    const basCadre = vue?.bottom ?? window.innerHeight;

    const declencher = (lot: HTMLElement[]) => {
      lot
        .sort((gauche, droite) => {
          const a = gauche.getBoundingClientRect();
          const b = droite.getBoundingClientRect();
          return a.top - b.top || a.left - b.left;
        })
        .forEach((tuile, rang) => {
          observateur.unobserve(tuile);
          tuile.style.setProperty('--rang', String(rang));
          tuile.classList.add('cascade--entre');
          /*
            La fin de l'animation, et pas celle d'un enfant : l'evenement
            remonte, et une image qui s'affiche a l'interieur de la tuile
            couperait sinon l'entree en cours.
          */
          const finie = (evenement: AnimationEvent) => {
            if (evenement.target !== tuile) return;
            tuile.removeEventListener('animationend', finie);
            tuile.classList.remove('cascade--entre');
            tuile.classList.add('cascade--vue');
            tuile.style.removeProperty('--rang');
          };
          tuile.addEventListener('animationend', finie);
        });
    };

    const observateur = new IntersectionObserver(
      (entrees) =>
        declencher(
          entrees
            .filter((entree) => entree.isIntersecting)
            .map((entree) => entree.target as HTMLElement)
            .filter((tuile) => !tuile.classList.contains('cascade--entre')),
        ),
      { root: cadre, rootMargin: '0px 0px -6% 0px', threshold: 0.01 },
    );

    /*
      La premiere vague part maintenant ; le reste attend l'observateur,
      qui prendra la suite a mesure qu'on descend.
    */
    const dedans = tuiles.filter((tuile) => {
      const rect = tuile.getBoundingClientRect();
      return rect.bottom > hautCadre && rect.top < basCadre;
    });

    tuiles.forEach((tuile) => observateur.observe(tuile));
    declencher(dedans);

    return () => observateur.disconnect();
  }, [nom, grille, cle, pret]);
}
